import {FilterFactory, InFilter, OperatorFilter, TermFilter} from '@dofleini/query-builder';
import {useUser} from '@dofleini/security';
import {CubaCIRuler, LatterAndNumberRuler, NameRuler} from '@dofleini/validator';
import {activityPlaces, proposalrequest} from '@mep/pae-commons';
import {Divider} from 'antd';
import i18n from 'i18next';
import cloneDeep from 'lodash/cloneDeep';
import set from 'lodash/set';
import React, {useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useQueries, useQuery} from 'react-query';

import CheckInput from '@/components/CheckInput';
import Editor from '@/components/Editor';
import FormTitleSession from '@/components/FormTitleSession';
import InfoBox from '@/components/InfoBox';
import {renderMunicipalitySelect} from '@/components/MunicipalitySelect';
import PhoneEditor from '@/components/PhoneNumberField/PhoneEditor';
import ProvinceSelect from '@/components/ProvinceSelect';
import RequiredText from '@/components/RequiredText/RequiredText';
import UploadFileDragger from '@/components/UploadFileDragger';
import {renderActivityPlaceLinkTypeSelect} from '@/modules/activityPlace/components/ActivityPlaceLinkTypeSelect';
import {renderProposalIsSoleOwner} from '@/modules/authentication/pages/SignUp/components/ProposalIsSoleOwner';
import {renderMapViewer} from '@/modules/company/components/OfficeMap/components/MapViewer';
import BankOfficeSelect from '@/modules/configuration/components/BankOfficeSelect';
import BankTypeSelect from '@/modules/configuration/components/BankTypeSelect';
import CNAESelect from '@/modules/configuration/components/CNAESelect';
import OrganizationSelect from '@/modules/configuration/components/OrganizationSelect';
import ProtectedAreaSelect from '@/modules/configuration/components/ProtectedAreaSelect';
import {useCountProtectedAreasByProvince} from '@/modules/configuration/hooks/useProtectedArea';
import {useSettings} from '@/modules/configuration/hooks/useSettings';
import ActivityPlacesSection from '@/modules/proposalRequest/components/ActivityPlacesSection';
import ModelProposalDownload from '@/modules/proposalRequest/components/ModelProposalDownload';
import ProposalActivitiesSelect from '@/modules/proposalRequest/components/ProposalActivitiesSelect';
import ProposalCapitalSummary from '@/modules/proposalRequest/components/ProposalCapitalSummary';
import ProposalIsReconversion from '@/modules/proposalRequest/components/ProposalIsReconversion';
import ProposalPrivatePartners from '@/modules/proposalRequest/components/ProposalPrivatePartners';
import ProposalSizeByWorkers from '@/modules/proposalRequest/components/ProposalSizeByWorkers';
import ProposalStatePartners from '@/modules/proposalRequest/components/ProposalStatePartners';
import {renderProposalWorkersOrigin} from '@/modules/proposalRequest/components/ProposalWorkersOrigin';
import ProposalWorkersProjection from '@/modules/proposalRequest/components/ProposalWorkersProjection';
import QuestionWithDetails, {renderQuestionWithDetails} from '@/modules/proposalRequest/components/QuestionWithDetails';
import {organizationsFields} from '@/modules/proposalRequest/contants/proposalOrganizations.form';
import {partnersFields} from '@/modules/proposalRequest/contants/proposalPartners.form';
import {
  CACHE_KEY_LIST_PROPOSAL_REQUEST,
  CACHE_KEY_PROPOSAL_REQUEST,
  MODELS_FILE_KEYS, PROPOSAL_SUBJECT_EXTENDED
} from '@/modules/proposalRequest/contants/proposalRequest';
import {fields, fieldsTCP} from '@/modules/proposalRequest/contants/proposalRequest.form';
import {useProposalCreation} from '@/modules/proposalRequest/contexts/ProposalRequestCreation';
import {usePartnersCapital} from '@/modules/proposalRequest/hooks/usePartnersCapital';
import {
  getSocialDenominationSuffix,
  handleIsSoleOwnerValue
} from '@/modules/proposalRequest/hooks/useSocialDenominationType';
import ProposalRequestApiService from '@/modules/proposalRequest/services/ProposalRequestApiService';
import createClearRenderView from '@/utils/createClearRenderView';
import {createFieldsHook} from '@/utils/createFieldsHook';
import createFormFieldset from '@/utils/createFormFieldset';
import {createGetOneHook} from '@/utils/createGetOneHook';
import createSearchService from '@/utils/createSearchService';
import {validations} from '@/utils/fields';
import {renderProvince} from '@/utils/location';

const {ACTIVITY_PLACE_LINK_TYPES} = activityPlaces;
const {STATE_MIPYME, CNA, SUBJECT_TYPE_ENUM, PROPOSAL_STATUS, ORGANIZATION_PMS} = proposalrequest;

export const useGetOneProposalRequest = createGetOneHook(ProposalRequestApiService, 'id', CACHE_KEY_PROPOSAL_REQUEST);
export const useGetOneProposalByCodeRequest = createGetOneHook(ProposalRequestApiService.getOneByCode, 'id', CACHE_KEY_PROPOSAL_REQUEST);
export const useGetOneProposalReducedRequest = createGetOneHook(ProposalRequestApiService.getOneReduced, 'id', `${CACHE_KEY_PROPOSAL_REQUEST}_reduced`);
export const finalExcludeStatus = [PROPOSAL_STATUS.DISSOLVED, PROPOSAL_STATUS.REJECTED, PROPOSAL_STATUS.CANCELED, PROPOSAL_STATUS.DISMISS, PROPOSAL_STATUS.EXPIRED];

export const useGetOneNotaryProposal = (proposalId) => {
  const queryConfig = useMemo(() => {
    return {enabled: !!(proposalId)};
  }, [proposalId]);
  
  const {isLoading, isError, error, data} = useQuery([CACHE_KEY_PROPOSAL_REQUEST, 'notary', proposalId], async () => {
    const {data} = await ProposalRequestApiService.getOneNotaryProposal({proposalId});
    return data;
  }, queryConfig);
  return {isLoading, isError, error, data};
};

// proposal request documentation step (step 1)
export const useFieldsBase = createFieldsHook('proposalRequest', fields);
export const useFieldsTCPBase = createFieldsHook('proposalRequest', fieldsTCP);
// proposal request partners step (step 2)
export const usePartnersFieldsBase = createFieldsHook('proposalRequest', partnersFields);
export const usePartnersFieldsTCPBase = createFieldsHook('proposalRequest', []);
// proposal request partners step (step 3)
export const useOrganizationsFieldsBase = createFieldsHook('proposalRequest', organizationsFields);
// proposal request documentation mapper fields step (step 1)
export const useFields = (isViewMode, form, columns) => {
  const fields = useFieldsBase(isViewMode, form, columns);
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');
  const {
    activities,
    mainActivity,
    address: location,
    subjectType,
    bankType,
    bankOffice,
    linkType
  } = form?.getFieldsValue();
  const {proposal, setProposal} = useProposalCreation();
  const {data, isLoading} = useCountProtectedAreasByProvince(location);
  const {data: settings} = useSettings();
  
  const type = useMemo(() => subjectType || user?.subjectType, [subjectType, user?.subjectType]);
  const isOnProtectedAreaActive = useMemo(() => proposal?.onProtectedArea?.active, [proposal?.onProtectedArea]);
  
  useEffect(() => {
    const exist = activities?.some(e => (e?.id || e) === mainActivity);
    if (exist) {
      const values = form.getFieldsValue(true);
      set(values, 'activities', activities?.filter(e => (e?.id || e) !== mainActivity));
      form.setFieldsValue(cloneDeep(values));
    }
  }, [activities, form, mainActivity]);
  
  return useMemo(() => {
    const fieldsMap = [...fields];
    const isStatePyme = [STATE_MIPYME, ORGANIZATION_PMS].includes(type);
    const isOrganizationPyme = [ORGANIZATION_PMS].includes(type);
    const isCNA = [CNA].includes(type);
    
    if (!isViewMode) {
      fieldsMap.splice(1, 0,
        {
          key: 'socialDenomination',
          colSpan: 3,
          label: t('proposalRequest.form.general.name.label'),
          placeholder: t('defaultPlaceholder'),
          hasFeedback: true,
          required: true,
          widgetProps: {
            suffix: getSocialDenominationSuffix(type, handleIsSoleOwnerValue(proposal?.isSoleOwner, user?.isSoleOwner)),
            size: 'large',
            autoFocus: true,
            type: 'text',
          },
          rules: [
            !proposal?._id && LatterAndNumberRuler,
            validations.bannedWordsCheck(settings)
          ]
        }
      );
      if (!isStatePyme)
        //
        fieldsMap.splice(3, 0,
          {
            colSpan: 3,
            key: 'partnersNonComplianceCriminalSanction',
            label: t('proposalRequest.form.general.sanctions.label'),
            widget: UploadFileDragger,
            required: true,
            widgetProps: {
              isImage: false,
              listType: 'picture',
              multiple: false,
              cropImage: false,
              translation: 'proposalRequest',
              descriptionText: 'uploadFile',
              maxCount: 1
            },
            extra: <InfoBox title={'proposalRequest.form.general.sanctions.help'} translate={'proposalRequest'}
              action={<ModelProposalDownload identifier={MODELS_FILE_KEYS.PARTNERS_VERIFICATION}/>}/>,
          });
      
      if (isOrganizationPyme)
        fieldsMap.splice(3, 0,
          {
            key: 'organization',
            label: t('proposalRequest.form.general.organization.label'),
            placeholder: 'defaultPlaceholder',
            required: true,
            colSpan: 3,
            widget: OrganizationSelect,
            widgetProps: {
              size: 'large'
            },
            extra: <InfoBox title={'proposalRequest.form.general.organization.help'} translate={'proposalRequest'}/>,
          });
      
      fieldsMap.splice(isStatePyme && !isOrganizationPyme ? 3 : 4, 0,
        {
          key: 'socialAddress',
          colSpan: 3,
          render: createFormFieldset({
            title: 'proposalRequest.form.general.socialAddress.label',
            help: 'proposalRequest.form.general.socialAddress.help',
            translation: 'proposalRequest'
          }),
        },
        {
          key: 'address.province',
          required: true,
          colSpan: 1,
          label: t('province'),
          widget: ProvinceSelect,
          initialValue: user?.address && user?.address.province,
          widgetProps: {
            size: 'large',
            onChange: () => form.setFieldsValue({bankType: '', bankOffice: ''})
          },
          viewWidget: ({value}) => renderProvince(value)
        },
        {
          key: 'address.municipality',
          label: t('municipality'),
          required: true,
          onlyEdit: true,
          initialValue: user?.address && user?.address.municipality,
          colSpan: 1,
          widgetProps: {
            size: 'large',
          },
          render: ({...rest}) => {
            return renderMunicipalitySelect(rest, location?.province);
          },
          rules: [validations.text()]
        },
        {
          key: 'linkType',
          label: t('activityPlace:linkType.label'),
          required: true,
          onlyEdit: true,
          colSpan: 1,
          widgetProps: {
            size: 'large',
          },
          render: renderActivityPlaceLinkTypeSelect
        },
        {
          key: 'address.abstract',
          colSpan: 3,
          label: t('address'),
          widget: 'textarea',
          initialValue: user?.address && user?.address.abstract,
          widgetProps: {autoSize: {minRows: 2, maxRows: 5}, size: 'large'},
          required: true,
          extra: <InfoBox title={'proposalRequest.form.general.address.help'} translate={'proposalRequest'}/>,
          className: 'form-view-mode-vertical-item'
        },
        {
          key: 'address.location',
          label: t('location'),
          colSpan: 3,
          onlyEdit: true,
          required: false,
          widgetProps: {
            isOnClickActive: true,
            province: location?.province,
            municipality: location?.municipality,
          },
          render: (props) => renderMapViewer(props, location?.province)
        },
        {
          key: 'divider',
          // eslint-disable-next-line react/display-name
          render: () => <Divider className="my-2 mb-3"/>
        },
        {
          label: t('proposalRequest.form.partners.bankType.label'),
          key: 'bankType',
          required: true,
          dependencies: ['address.province'],
          widget: BankTypeSelect,
          colSpan: 3,
          extra: <InfoBox title={'proposalRequest.form.partners.bankType.help'} translate={'proposalRequest'}
            translateValues={{
              type: t(`types.${proposal?.subjectType}`)
            }}/>,
          widgetProps: {
            size: 'large',
            disabled: !!bankOffice,
            province: location?.province
          }
        },
        {
          label: t('proposalRequest.form.partners.bankOffice.label'),
          key: 'bankOffice',
          required: true,
          widget: BankOfficeSelect,
          colSpan: 3,
          dependencies: ['bankType'],
          extra: <InfoBox title={'proposalRequest.form.partners.bankOffice.help'} translate={'proposalRequest'}
            translateValues={{
              type: t(`types.${proposal?.subjectType}`)
            }}/>,
          widgetProps: {
            disabled: !bankType,
            bankType: bankType,
            size: 'large',
            filters: new OperatorFilter({
              type: 'AND',
              filters: [
                new TermFilter({field: 'bank.code', value: bankType}),
                new TermFilter({field: 'address.province', value: location?.province}),
                new InFilter({field: 'applyFor', value: [PROPOSAL_SUBJECT_EXTENDED.MIPYMES]})
              ]
            }).toQuery(),
          },
        },
      );
      
      if (linkType === ACTIVITY_PLACE_LINK_TYPES.OTHER)
        fieldsMap.splice(isStatePyme && !isOrganizationPyme ? 7 : 8, 0,
          {
            key: 'linkTypeDescription',
            colSpan: 3,
            label: t('common:description'),
            widget: 'textarea',
            initialValue: '',
            widgetProps: {autoSize: {minRows: 2, maxRows: 5}, size: 'large'},
            required: true,
            extra: <InfoBox title={'activityPlace:description'} translate={'activityPlace'}/>,
            className: 'form-view-mode-vertical-item',
          });
      
      if (!!isOnProtectedAreaActive || (!isLoading && data?.total))
        fieldsMap.splice(7, 0, {
          key: 'onProtectedArea',
          colSpan: 3,
          dependencies: [['address', 'province']],
          label: t('proposalRequest.form.general.onProtectedArea.label'),
          render: renderQuestionWithDetails,
          extra: <InfoBox title={'proposalRequest.form.general.onProtectedArea.help'} translate={'proposalRequest'}/>,
          widgetProps: {
            withFile: true,
            extraLabel: t('proposalRequest.form.general.onProtectedArea.name'),
            fileLabel: 'proposalRequest.form.general.onProtectedArea.fileLabel',
            fileUploadLabel: 'proposalRequest.form.general.onProtectedArea.uploadLabel',
            placeholderText: 'selectPlaceholder',
            translate: 'proposalRequest',
            size: 'large',
            filters: new OperatorFilter({
              type: 'AND', filters: [
                new TermFilter({field: 'address.province', value: location?.province}),
                new TermFilter({field: 'municipalities', value: location?.municipality})
              ]
            }).toQuery(),
            customWhereCmp: ProtectedAreaSelect
          },
        }
        );
      
      const isReconversionIndex = (!isLoading && data?.total) ? 12 : 11;
      
      fieldsMap.splice(isReconversionIndex, 0,
        {
          key: 'isReconversion',
          widget: ProposalIsReconversion,
          colSpan: 3,
          widgetProps: {
            translate: 'proposalRequest',
            proposal: proposal || {},
            setProposal
          },
          initialValue: false
        });
      
      // find the index, avoid manual changes
      const activityDeclarationIndex = fieldsMap.findIndex((e) => e.key === 'activityDeclaration');
      
      fieldsMap.splice(activityDeclarationIndex + 1, 0, {
        key: 'socialObject',
        label: t('proposalRequest.form.general.socialObject.label'),
        widget: Editor,
        colSpan: 3,
        widgetProps: {size: 'large'},
        required: true,
        className: 'form-view-mode-vertical-item'
      },
      {
        key: 'mainActivity',
        colSpan: 3,
        label: t('proposalRequest.form.general.mainActivity.label'),
        placeholder: t('selectPlaceholder'),
        required: true,
        dependencies: ['activities'],
        widget: CNAESelect,
        widgetProps: {
          size: 'large',
          filters: !isStatePyme ? new TermFilter({field: 'isProhibited', value: false}).toQuery() : null
        },
      },
      {
        key: 'otherActivities',
        label: t('proposalRequest.form.general.activities.textLabel'),
        colSpan: 3,
        widget: Editor,
        widgetProps: {size: 'large'},
        className: 'form-view-mode-vertical-item'
      },
      {
        key: 'activities',
        colSpan: 3,
        label: t('proposalRequest.form.general.activities.label'),
        placeholder: t('selectPlaceholder'),
        required: false,
        dependencies: ['mainActivity'],
        widget: ProposalActivitiesSelect,
        extra: <InfoBox title={'proposalRequest.form.general.activities.help'} translate={'proposalRequest'}/>,
        widgetProps: {
          filters: !isStatePyme ? new TermFilter({field: 'isProhibited', value: false}).toQuery() : null,
          mainActivity: mainActivity,
          size: 'large'
        },
      },
      {
        key: 'projectDescription',
        label: t('proposalRequest.form.general.projectDescription.label'),
        widget: Editor,
        colSpan: 3,
        widgetProps: {size: 'large'},
        required: true,
        extra: <InfoBox title={'proposalRequest.form.general.projectDescription.help'}
          translate={'proposalRequest'}/>,
        className: 'form-view-mode-vertical-item'
      },
      {
        key: 'statutes',
        colSpan: 3,
        label: t('proposalRequest.form.general.statutes.label'),
        widget: UploadFileDragger,
        required: true,
        widgetProps: {
          isImage: false,
          listType: 'picture',
          multiple: false,
          cropImage: false,
          translation: 'proposalRequest',
          descriptionText: 'uploadFile',
          maxCount: 1
        },
        extra: <InfoBox title={'proposalRequest.form.general.statutes.help'} translate={'proposalRequest'}
          action={<ModelProposalDownload
            identifier={isCNA ? MODELS_FILE_KEYS.CNA_STATUTES : MODELS_FILE_KEYS.MIPYMES_STATUTES}/>}/>,
      },);
      
      if (isCNA) {
        // find the index, avoid manual changes
        const statuteIndex = fieldsMap.findIndex((e) => e.key === 'statutes');
        fieldsMap.splice(statuteIndex + 1, 0,
          {
            key: 'previousCNAData',
            widget: QuestionWithDetails,
            colSpan: 3,
            widgetProps: {
              label: 'proposalRequest.previousCNAData.label',
              extraLabel: 'proposalRequest.previousCNAData',
              translate: 'proposalRequest',
              fileUploadLabel: 'uploadFile',
              size: 'large',
              options: [
                {field: 'option1', required: true, type: 'file'},
                {field: 'option2', required: true, type: 'text'},
                {field: 'option3', required: true, type: 'text'},
                {field: 'option4', required: true, type: 'file'},
                {field: 'option5', required: true, type: 'file'}
              ],
              extra: proposal
            },
            extra: <InfoBox title={'proposalRequest.previousCNAData.help'} translate={'proposalRequest'}/>
          });
      }
      
      if (!isStatePyme)
        fieldsMap.splice(
          (fieldsMap.length), 0, {
            key: 'isNotFunctionary',
            label: t('proposalRequest.form.isNotFunctionary.label'),
            required: true,
            colSpan: 3,
            widget: CheckInput,
            valuePropName: 'checked',
            widgetProps: {
              translate: 'proposalRequest',
              title: 'proposalRequest.form.isNotFunctionary.title',
            },
            rules: [{
              validator: (_, value) =>
                value ? Promise.resolve() : Promise.reject(new Error(t('proposalRequest.form.isNotFunctionary.required'))),
            }]
          });
      
      fieldsMap.splice(
        (fieldsMap.length), 0, {
          key: 'subjectType',
          initialValue: proposal?.subjectType || user?.subjectType,
          noStyle: true,
          widgetProps: {
            hidden: true
          },
          required: true
        });
    }
    return fieldsMap;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, type, isViewMode, t, user?.address, user?.idNumber, data?.total, location, activities, mainActivity, settings, isOnProtectedAreaActive]);
};
// proposal request partner mapper fields step (step 2)
export const usePartnersFields = (isViewMode, form, columns) => {
  const {proposal, setProposal} = useProposalCreation();
  const {user} = useUser();
  const {t} = useTranslation('proposalRequest');
  const fields = usePartnersFieldsBase(isViewMode, form, columns);
  const {miPymeExpectedWorkers, partners, statePartners} = form?.getFieldsValue();
  
  const isStateMipyme = [STATE_MIPYME, ORGANIZATION_PMS].includes(proposal?.subjectType);
  
  const {totalAmount} = usePartnersCapital((isStateMipyme ? statePartners : partners) || []);
  
  const isSoleOwner = useMemo(() => handleIsSoleOwnerValue(proposal?.isSoleOwner, proposal?.owner?.isSoleOwner || user?.isSoleOwner), [proposal?.isSoleOwner, proposal?.owner?.isSoleOwner, user?.isSoleOwner]);
  
  return useMemo(() => {
    const fieldsMap = [...fields];
    const isCNA = CNA === proposal?.subjectType;
    
    if (!isViewMode) {
      if (!isStateMipyme) {
        if (!isCNA)
          fieldsMap.splice(1, 0, {
            key: 'isSoleOwner',
            colSpan: 3,
            render: renderProposalIsSoleOwner,
          });
        
        fieldsMap.splice(
          1, 0,
          {
            key: 'activityPlace',
            colSpan: 3,
            widget: ActivityPlacesSection,
            widgetProps: {
              proposal,
            },
          },
          {
            key: 'capitalTitle',
            colSpan: 3,
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession
              translate={'proposalRequest'}
              title={'proposalRequest.form.partners.socialCapital.title'}
              description={'proposalRequest.form.partners.socialCapital.help'}
              divider
            />
          },
          {
            key: 'showSocialCapitalAmount',
            colSpan: 3,
            label: t('proposalRequest.form.partners.socialCapital.title'),
            widget: ProposalCapitalSummary,
            dependencies: ['partners'],
            widgetProps: {
              partners,
              isCNA
            },
            rules: [
              {
                validator: () => {
                  return new Promise((resolve, reject) => {
                    if (!totalAmount) {
                      if (isCNA)
                        reject(new Error(i18n.t('proposalRequest:proposalRequest.form.partners.socialCapital.required',
                          {label: i18n.t(`proposalRequest:proposalRequest.form.partners.socialCapital.${isCNA ? 'initialAmountOfWork' : 'monetaryAmountSummary'}`)})));
                    } else {
                      resolve();
                    }
                  });
                }
              },
            ]
          },
          {
            key: 'title',
            colSpan: 3,
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession
              translate={'proposalRequest'}
              title={isSoleOwner && !isCNA ? 'proposalRequest.form.partners.title' : 'proposalRequest.form.partners.title_plural'}
              description={'proposalRequest.form.partners.help'} divider/>
          },
          {
            key: 'partners',
            colSpan: 3,
            required: true,
            widget: ProposalPrivatePartners,
            widgetProps: {
              proposal,
              setProposal
            },
            initialValue: [],
            rules: [
              {
                validator: (rule, value) => {
                  // Do async validation to check if username already exists
                  // Use setTimeout to emulate api call
                  return new Promise((resolve, reject) => {
                    const someBlank = value?.some(e => (!e?.contribution && (!e?.goods || !e?.goods?.length)));
                    const someBlankBaseImport = value?.some(e => (!e?.importBase && !e.isRetired));
                    if (isCNA) {
                      resolve();
                    } else if (someBlank) {
                      reject(new Error(i18n.t('proposalRequest:proposalRequest.form.partners.contributionIsRequired')));
                    } else if (someBlankBaseImport) {
                      reject(new Error(i18n.t('proposalRequest:proposalRequest.form.partners.importBaseRequired')));
                    } else {
                      resolve();
                    }
                  });
                }
              },
            ]
          },
        );
        if (!isCNA) {
          fieldsMap.splice(7, 0,
            {
              key: 'workers',
              // eslint-disable-next-line react/display-name
              render: () => <FormTitleSession translate={'proposalRequest'} title={'steps.workers'}/>
            },
            {
              key: 'miPymeExpectedWorkers',
              colSpan: 3,
              label: t('proposalRequest.form.partners.workersProjection.label'),
              widget: ProposalWorkersProjection,
              widgetProps: {
                max: isStateMipyme ? null : 100
              },
              placeholder: t('amountPlaceHolder'),
              required: true
            },
            {
              key: 'workersOrigin',
              colSpan: 3,
              dependencies: ['miPymeExpectedWorkers'],
              label: t('proposalRequest.form.partners.workersOrigin.label'),
              render: renderProposalWorkersOrigin,
              initialValue: {},
              placeholder: t('amountPlaceHolder'),
              required: false
            },
            {
              key: 'miPymeExpectedWorkersSize',
              colSpan: 3,
              dependencies: ['miPymeExpectedWorkers', 'workersOrigin'],
              // eslint-disable-next-line react/display-name
              render: () => {
                const total = (miPymeExpectedWorkers || 0) + (partners?.length || 0);
                return <>
                  <RequiredText required={false} value={t('proposalRequest.form.partners.sizeOfBusiness.label')}/>
                  <ProposalSizeByWorkers
                    type={proposal?.subjectType}
                    value={total}
                    total={Math.max(total || 0, 100)}/>
                </>;
              }
            }
          );
        } else {
          fieldsMap.splice(6, 0, {
            key: 'cnaPartnersListFile',
            colSpan: 3,
            label: t('proposalRequest.form.general.cnaPartnersListFile.label'),
            widget: UploadFileDragger,
            required: false,
            widgetProps: {
              isImage: false,
              listType: 'picture',
              multiple: false,
              cropImage: false,
              translation: 'proposalRequest',
              descriptionText: 'uploadFile',
              maxCount: 1
            },
            extra: <InfoBox
              title={'proposalRequest.form.general.cnaPartnersListFile.help'}
              translate={'proposalRequest'}
              action={<ModelProposalDownload
                identifier={MODELS_FILE_KEYS.CNA_PARTNERS}/>}
            />,
          });
        }
      } else {
        fieldsMap.splice(
          1, 0,
          {
            key: 'activityPlace',
            colSpan: 3,
            widget: ActivityPlacesSection,
            widgetProps: {
              proposal,
            },
          },
          {
            key: 'capitalTitle',
            colSpan: 3,
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession
              translate={'proposalRequest'}
              title={'proposalRequest.form.partners.socialCapital.title'}
              description={'proposalRequest.form.partners.socialCapital.help'}
              divider
            />
          },
          {
            key: 'showSocialCapitalAmount',
            colSpan: 3,
            label: t('proposalRequest.form.partners.socialCapital.title'),
            widget: ProposalCapitalSummary,
            dependencies: ['statePartners'],
            widgetProps: {
              partners: statePartners,
              isCNA,
              proposal
            },
            rules: [
              {
                validator: () => {
                  return new Promise((resolve, reject) => {
                    if (!totalAmount) {
                      reject(new Error(i18n.t('proposalRequest:proposalRequest.form.partners.socialCapital.required',
                        {label: i18n.t('proposalRequest:proposalRequest.form.partners.socialCapital.monetaryAmountSummary')})));
                    } else {
                      resolve();
                    }
                  });
                }
              },
            ]
          },
          {
            key: 'divider',
            // eslint-disable-next-line react/display-name
            render: () => <Divider className="my-2 mb-3"/>
          },
          {
            key: 'title',
            colSpan: 3,
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession
              translate={'proposalRequest'}
              title={isSoleOwner ? 'proposalRequest.form.partners.title' : 'proposalRequest.form.partners.title_plural'}
              description={'proposalRequest.form.partners.help'} divider/>
          },
          {
            key: 'statePartners',
            colSpan: 3,
            required: true,
            widget: ProposalStatePartners,
            initialValue: [],
            widgetProps: {
              proposal,
              setProposal
            },
            rules: [
              {
                validator: (rule, value) => {
                  return new Promise((resolve, reject) => {
                    const someBlank = value?.some(e => (!e?.contribution && (!e?.goods || !e?.goods?.length)));
                    const withOutRepresentative = value.some(e => !e?.representative);
                    if (value && (withOutRepresentative || someBlank)) {
                      if (someBlank)
                        reject(new Error(i18n.t('proposalRequest:proposalRequest.form.partners.contributionIsRequired')));
                      else
                        reject();
                    } else {
                      resolve();
                    }
                  });
                }
              },
            ]
          },
          {
            key: 'stateBoss',
            colSpan: 3,
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession translate={'proposalRequest'}
              title={'proposalRequest.form.partners.statePartnerBoss.title'}/>
          },
          {
            key: 'statePartnerBoss.firstName',
            colSpan: 1.5,
            label: t('firstName'),
            onlyEdit: true,
            required: true,
            extra: <InfoBox title={'proposalRequest.form.partners.statePartnerBoss.firstNameHelp'}
              translate={'proposalRequest'}/>,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.middleName',
            colSpan: 1.5,
            label: t('middleName'),
            onlyEdit: true,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.lastName',
            colSpan: 1.5,
            label: t('lastName'),
            onlyEdit: true,
            required: true,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.secondName',
            colSpan: 1.5,
            label: t('secondName'),
            onlyEdit: true,
            required: false,
            widgetProps: {
              size: 'large',
            },
            rules: [NameRuler],
          },
          {
            key: 'statePartnerBoss.idNumber',
            colSpan: 1.5,
            widgetProps: {
              size: 'large',
            },
            label: t('proposalRequest.form.partners.statePartnerBoss.dni.placeholder'),
            required: true,
            rules: [CubaCIRuler],
          },
          {
            key: 'statePartnerBoss.email',
            widgetProps: {
              size: 'large',
            },
            colSpan: 1.5,
            label: t('email'),
            renderView: createClearRenderView(),
            rules: [{required: true}, {type: 'email'}],
          },
          {
            key: 'statePartnerBoss.phone',
            colSpan: 3,
            required: true,
            label: t('phones'),
            widgetProps: {
              size: 'large',
            },
            rules: [validations.listPhones(), validations.phoneValidator()],
            widget: PhoneEditor
          },
          {
            key: 'workers',
            // eslint-disable-next-line react/display-name
            render: () => <FormTitleSession translate={'proposalRequest'} title={'steps.workers'}/>
          },
          {
            key: 'miPymeExpectedWorkers',
            colSpan: 3,
            label: t('proposalRequest.form.partners.workersProjection.title'),
            widget: ProposalWorkersProjection,
            widgetProps: {
              max: 100
            },
            placeholder: t('amountPlaceHolder'),
            required: true
          },
        );
      }
      fieldsMap.splice(
        (fieldsMap.length), 0, {
          key: 'subjectType',
          initialValue: proposal?.subjectType || user?.subjectType,
          noStyle: true,
          widgetProps: {
            hidden: true
          },
          required: true
        }, {
          key: 'owner',
          noStyle: true,
          widgetProps: {
            hidden: true
          }
        });
    }
    return fieldsMap;
  }, [fields, isStateMipyme, isViewMode, miPymeExpectedWorkers, partners, proposal, setProposal, statePartners, t, totalAmount, isSoleOwner, user?.subjectType]);
};
// proposal request partner mapper fields step (step 3)
export const useOrganizationsFields = (isViewMode, form, columns) => {
  const {t} = useTranslation('proposalRequest');
  const {proposal} = useProposalCreation();
  const fields = useOrganizationsFieldsBase(isViewMode, form, columns);
  
  return useMemo(() => {
    const fieldsMap = [...fields];
    
    if (!isViewMode) {
      fieldsMap.splice(
        0, 0,
        {
          key: 'groupsHelp',
          colSpan: 3,
          // eslint-disable-next-line react/display-name
          render: () => <InfoBox translate={'proposalRequest'} title={'proposalRequest.form.organizations.help'}
            translateValues={{type: t(`proposalRequest.types.${proposal?.subjectType}`)}}/>
        }
      );
    }
    return fieldsMap;
  }, [fields, isViewMode, proposal?.subjectType, t]);
};

export const useIsValidPartnerIdentifier = (identifier, proposalId, subjectType, config = {}) => {
  const {isLoading, isError, error, data} = useQuery([identifier, proposalId, subjectType], async () => {
    if (identifier) {
      const {data} = await ProposalRequestApiService.checkPartner({identifier, proposalId, subjectType});
      return (data);
    }
  }, config);
  return {isLoading, isError, error, data};
};

export const useProposalsByTypes = (filters, search, service) => {
  const {isAuthenticated} = useUser();
  
  const queries = SUBJECT_TYPE_ENUM?.map((type) => ({
    queryKey: [CACHE_KEY_LIST_PROPOSAL_REQUEST, type, filters, search, service],
    queryFn: async () => {
      const {headers} = await service({
        page: 1,
        size: 1,
        search,
        filters: FilterFactory.add(filters, new TermFilter({field: 'subjectType', value: type})).toQuery(),
      });
      const total = Number(headers['x-total-count'] || 0);
      return {[type]: total};
    },
    refetchOnWindowFocus: false,
    enabled: !!isAuthenticated
  }));
  
  const responses = useQueries(queries);
  
  return {
    data: responses.map((r) => {
      return r?.data;
    })?.reduce((r, e) => {
      return ({...r, ...e});
    }, {}),
    isLoading: responses.some(e => e?.isLoading)
  };
};

export const useProposalsByStatus = (filters, search, steps) => {
  const {isAuthenticated} = useUser();
  
  const queries = steps?.map((status) => {
    return ({
      queryKey: [CACHE_KEY_LIST_PROPOSAL_REQUEST, status, filters, search],
      queryFn: async () => {
        const {headers} = await ProposalRequestApiService.search({
          page: 1,
          size: 1,
          search,
          filters: FilterFactory.add(
            FilterFactory.removeByFilter(filters, {field: 'status', type: 'TERM'}),
            new TermFilter({field: 'status', value: status})).toQuery(),
        });
        const total = Number(headers['x-total-count'] || 0);
        return {[status]: total};
      },
      refetchOnWindowFocus: false,
      keepPreviousData: true,
      enabled: !!isAuthenticated
    });
  });
  
  const responses = useQueries(queries);
  
  const data = responses.map((r) => r?.data)?.reduce((r, e) => ({...r, ...e}), {});
  
  return {
    data,
    total: steps?.reduce((t, e) => {
      if (![...finalExcludeStatus, PROPOSAL_STATUS.ARCHIVED, PROPOSAL_STATUS.ACCEPTED].includes(e))
        t += data?.[e] || 0;
      return t;
    }, 0),
    isLoading: responses.some(e => e?.isLoading)
  };
};

export const useProposalsSummary = (filters, search) => {
  const {isAuthenticated} = useUser();
  const searchService = createSearchService(ProposalRequestApiService.aggregate);
  
  const queries = [
    {
      queryKey: [CACHE_KEY_LIST_PROPOSAL_REQUEST, filters, search, 'count'],
      queryFn: async () => searchService('count', {filters}),
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      enabled: !!isAuthenticated
    },
    {
      queryKey: [CACHE_KEY_LIST_PROPOSAL_REQUEST, filters, search, 'partnersCount'],
      queryFn: async () => searchService('partnersCount', {filters}),
      refetchOnWindowFocus: true,
      keepPreviousData: true,
      enabled: !!isAuthenticated
    }
  ];
  
  const responses = useQueries(queries);
  
  const data = responses.map((r) => r?.data).map((r) => r?.data?.[0]?.total || 0);
  
  return {
    data,
    isLoading: responses.some(e => e?.isLoading)
  };
};
